import { Box3, Matrix4, OrthographicCamera, Quaternion, Vector3 } from 'three';

/**
 * The editor's world (Z up, Front looks along +Y) described in the Navigation
 * Library's frame (X right, Y up, Z out of the screen), column-major.
 */
export const NAVLIB_COORDINATE_SYSTEM: readonly number[] = [1, 0, 0, 0, 0, 0, -1, 0, 0, 1, 0, 0, 0, 0, 0, 1];

/**
 * Camera pose of the Front view in world coordinates: X right, Z up, looking
 * along +Y. The driver derives its Top, Right, ISO, ... buttons from this.
 */
export const NAVLIB_FRONT_VIEW: readonly number[] = [1, 0, 0, 0, 0, 0, 1, 0, 0, -1, 0, 0, 0, 0, 0, 1];

/** The camera and orbit target the driver moves. */
export interface SpaceMouseRig {
  camera: OrthographicCamera;
  controls: { target: Vector3 };
}

const MIN_ZOOM = 1e-4;
const MAX_ZOOM = 1e6;

/**
 * Camera-to-parent matrix of the view. The driver works in the camera's parent
 * space (the editor's view frame), which is world space unless the view is turned.
 */
export function viewMatrix(camera: OrthographicCamera): number[] {
  camera.updateMatrix();
  return camera.matrix.toArray();
}

/**
 * Places the camera at the pose the driver computed. The orbit target moves
 * onto the new line of sight at its old distance and `up` follows the camera,
 * so the controls' per-frame lookAt(target) keeps the pose as written.
 */
export function applyViewMatrix(rig: SpaceMouseRig, matrix: readonly number[]): void {
  const { camera, controls } = rig;
  const position = new Vector3();
  const rotation = new Quaternion();
  new Matrix4().fromArray(matrix as number[]).decompose(position, rotation, new Vector3());

  const distance = camera.position.distanceTo(controls.target) || 1;
  const forward = new Vector3(0, 0, -1).applyQuaternion(rotation);

  camera.position.copy(position);
  camera.quaternion.copy(rotation);
  camera.up.set(0, 1, 0).applyQuaternion(rotation);
  controls.target.copy(position).addScaledVector(forward, distance);
  camera.updateMatrixWorld();
}

/** The visible box in view coordinates: `[left, bottom, -far, right, top, far]`. */
export function viewExtents(camera: OrthographicCamera): number[] {
  const zoom = camera.zoom;
  return [
    camera.left / zoom,
    camera.bottom / zoom,
    -camera.far,
    camera.right / zoom,
    camera.top / zoom,
    camera.far,
  ];
}

/**
 * Zooms to the extents the driver asks for. The engine owns the frustum size
 * (it follows the canvas aspect), so the zoom carries the scale and an
 * off-centre box becomes a pan.
 */
export function applyViewExtents(rig: SpaceMouseRig, extents: readonly number[]): void {
  const { camera, controls } = rig;
  const width = extents[3] - extents[0];
  if (!(width > 0)) return;

  camera.zoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, (camera.right - camera.left) / width));
  camera.updateProjectionMatrix();

  const centreX = (extents[0] + extents[3]) / 2;
  const centreY = (extents[1] + extents[4]) / 2;
  if (centreX === 0 && centreY === 0) return;

  camera.updateMatrix();
  const offset = new Vector3(centreX, centreY, 0).transformDirection(camera.matrix)
    .multiplyScalar(Math.hypot(centreX, centreY));
  camera.position.add(offset);
  controls.target.add(offset);
  camera.updateMatrixWorld();
}

/** `[minX, minY, minZ, maxX, maxY, maxZ]`, or null for an empty box. */
export function boxExtents(box: Box3): number[] | null {
  if (box.isEmpty()) return null;
  return [box.min.x, box.min.y, box.min.z, box.max.x, box.max.y, box.max.z];
}
